import React, { useState, useRef, useEffect } from "react";
import Tooltip from "@mui/material/Tooltip";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useIsTruncated } from "../../hooks/useIsTruncated";
import { ReactComponent as FolderSvg } from "../../assets/images/folder-open-line.svg";
import { ReactComponent as EditSvg } from "../../assets/images/edit-2-line.svg";
import { ReactComponent as DeleteBinSvg } from "../../assets/images/delete-bin-line.svg";
import { useTranslation } from "react-i18next";
import { useDatabase } from "./DatabaseContext";
import DeleteModal from "../modals/DeleteModal";

interface FolderItemProps {
    id: string;
    name: string;
    onEdit?: (id: string, newName: string) => void;
    onDelete?: (id: string) => void;
    onSelect?: (id: string) => void;
    isSelected?: boolean;
}

const FolderItem: React.FC<FolderItemProps> = ({ id, name, onEdit, onDelete, onSelect, isSelected }) => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const [isHovered, setIsHovered] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [editName, setEditName] = useState(name);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

    const textRef = useRef<HTMLSpanElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const isTruncated = useIsTruncated(textRef, [name]);

    const { isRefreshing } = useDatabase();

    // Disable drag while the folder is being renamed so typing inside the input
    // never triggers a reorder.
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id,
        disabled: editMode,
    });

    const sortableStyle: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : undefined,
    };

    useEffect(() => {
        if (editMode && inputRef.current) {
            inputRef.current.focus();
        }
    }, [editMode]);

    const handleEditClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        setEditMode(true);
    };

    const handleDelete = (e: React.MouseEvent) => {
        if (isRefreshing) return;
        e.stopPropagation();
        setIsDeleteModalOpen(true);
    };

    const handleDeleteConfirm = () => {
        if (onDelete) onDelete(id);
    };

    const handleSelect = () => {
        if (isRefreshing) return;
        if (onSelect) onSelect(id);
    };

    const handleEditSubmit = () => {
        if (isRefreshing) return;
        if (onEdit && editName.trim() !== "" && editName !== name) {
            onEdit(id, editName.trim());
        }
        setEditMode(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (isRefreshing) return;
        if (e.key === "Enter") {
            handleEditSubmit();
        } else if (e.key === "Escape") {
            setEditMode(false);
            setEditName(name);
        }
    };

    const folderNameDisplay = (
        <span ref={textRef} className={`folder-name-text ${isSelected ? "underline" : ""}`}>
            {name}
        </span>
    );

    return (
        <>
            <li
                ref={setNodeRef}
                style={sortableStyle}
                {...attributes}
                {...listeners}
                className={`database-folder-sidebar-links ${isSelected ? "selected-folder" : ""}`}
                onClick={handleSelect}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                <div className="database-folder-content">
                    <div className="database-folder-name">
                        <FolderSvg className="database-folder-sidebar-icon" />
                        {editMode ? (
                            <input
                                ref={inputRef}
                                className="folder-name-input"
                                value={editName}
                                onChange={(e) => setEditName(e.target.value)}
                                onBlur={handleEditSubmit}
                                onKeyDown={handleKeyDown}
                            />
                        ) : isTruncated ? (
                            <Tooltip title={name} arrow>
                                {folderNameDisplay}
                            </Tooltip>
                        ) : (
                            folderNameDisplay
                        )}
                    </div>

                    {isHovered && !editMode && (
                        <div className="database-folder-actions">
                            <Tooltip title={t("editFolderName")} arrow>
                                <EditSvg className="database-folder-action-icon" onClick={handleEditClick} />
                            </Tooltip>
                            <Tooltip title={t("deleteFolder")} arrow>
                                <DeleteBinSvg
                                    className="database-folder-action-icon delete-icon"
                                    onClick={handleDelete}
                                />
                            </Tooltip>
                        </div>
                    )}
                </div>
            </li>

            <DeleteModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={handleDeleteConfirm}
                message={t("deleteFolderConfirmation")}
                title={t("deleteFolder")}
            />
        </>
    );
};

export default FolderItem;
